import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Eye, EyeOff, Users, Building2, Eye as EyeIcon } from 'lucide-react';

const LOGO_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/47eefe68-1dae-481a-a902-450e03018adb.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1741940513798-4ce04b95ffda?crop=entropy&cs=srgb&fm=jpg&q=85";

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'admin' || user.role === 'assignor') {
        navigate('/dashboard');
      } else if (user.role === 'referee') {
        navigate('/referee-portal');
      } else {
        navigate('/client-portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <img src={LOGO_URL} alt="Official With The Whistle" fetchpriority="high" decoding="async" loading="eager" className="w-44 h-44 md:w-56 md:h-56 mb-4 drop-shadow-[0_0_40px_rgba(245,158,11,0.5)]" />
          <h1 className="text-4xl font-heading font-bold text-white uppercase tracking-wider text-center">
            Official With The Whistle
          </h1>
          <p className="text-zinc-400 mt-4 text-center max-w-md">
            Professional referee management and invoicing for sports organizations
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-28 h-28 md:w-32 md:h-32" />
          </div>
          
          <div>
            <h2 className="text-3xl font-heading font-bold uppercase tracking-wide">Sign In</h2>
            <p className="text-muted-foreground mt-2">Enter your credentials to access your account</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-900 text-red-400 rounded-sm">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
                className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm transition-transform active:scale-95"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-background text-xs text-zinc-500 uppercase tracking-widest">Or</span>
            </div>
          </div>

          {/* Google Sign-in */}
          <Button
            type="button"
            onClick={() => googleLogin()}
            data-testid="google-login-btn"
            className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-bold uppercase tracking-wide rounded-sm flex items-center justify-center gap-3 transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-white hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const qRole = searchParams.get('role');
  const initialRole = (qRole === 'referee' || qRole === 'client' || qRole === 'fan') ? qRole : 'client';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: initialRole,
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const qr = searchParams.get('role');
    if (qr === 'referee' || qr === 'client' || qr === 'fan') {
      setFormData(prev => ({ ...prev, role: qr }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        phone: formData.phone || null
      });
      
      if (user.role === 'admin' || user.role === 'assignor') {
        navigate('/dashboard');
      } else if (user.role === 'referee') {
        navigate('/referee-portal');
      } else {
        navigate('/client-portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <img src={LOGO_URL} alt="Official With The Whistle" fetchpriority="high" decoding="async" loading="eager" className="w-44 h-44 md:w-56 md:h-56 mb-4 drop-shadow-[0_0_40px_rgba(245,158,11,0.5)]" />
          <h1 className="text-4xl font-heading font-bold text-white uppercase tracking-wider text-center">
            Official With The Whistle
          </h1>
          <p className="text-zinc-400 mt-4 text-center max-w-md">
            Professional referee management and invoicing for sports organizations
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-28 h-28 md:w-32 md:h-32" />
          </div>
          
          <div>
            <h2 className="text-3xl font-heading font-bold uppercase tracking-wide">Create Account</h2>
            <p className="text-muted-foreground mt-2">Join Official With The Whistle</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-900 text-red-400 rounded-sm">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Full Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                data-testid="register-name-input"
                className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                data-testid="register-email-input"
                className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Phone (Optional)</Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                data-testid="register-phone-input"
                className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">I am signing up as a…</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="role-referee-btn"
                  onClick={() => setFormData({ ...formData, role: 'referee' })}
                  className={`flex flex-col items-center gap-1.5 p-3 border-2 rounded-sm transition-all ${
                    formData.role === 'referee'
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-wide">Referee</p>
                    <p className="text-[9px] text-zinc-500 mt-0.5">Work games</p>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="role-client-btn"
                  onClick={() => setFormData({ ...formData, role: 'client' })}
                  className={`flex flex-col items-center gap-1.5 p-3 border-2 rounded-sm transition-all ${
                    formData.role === 'client'
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-wide">Client</p>
                    <p className="text-[9px] text-zinc-500 mt-0.5">Book officials</p>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="role-fan-btn"
                  onClick={() => setFormData({ ...formData, role: 'fan' })}
                  className={`flex flex-col items-center gap-1.5 p-3 border-2 rounded-sm transition-all ${
                    formData.role === 'fan'
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  <EyeIcon className="w-5 h-5" />
                  <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-wide">Fan</p>
                    <p className="text-[9px] text-zinc-500 mt-0.5">Watch & listen</p>
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Password</Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  data-testid="register-password-input"
                  className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Confirm Password</Label>
              <Input
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                data-testid="register-confirm-password-input"
                className="bg-zinc-900 border-zinc-800 focus:border-white h-12 rounded-sm"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full h-12 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm transition-transform active:scale-95"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
            <div className="relative flex justify-center"><span className="px-3 bg-background text-xs text-zinc-500 uppercase tracking-widest">Or</span></div>
          </div>

          {/* Google Sign-up */}
          <Button
            type="button"
            onClick={() => googleLogin(formData.role)}
            data-testid="google-register-btn"
            className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-bold uppercase tracking-wide rounded-sm flex items-center justify-center gap-3 transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-white hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
